// Single source of truth for chart colours.
//
// Goal: any given mediatype / sentiment / social-source must look identical
// across every chart, AND adjacent categories must be visually distinct.
//
// Hues are spaced around the wheel; for the 12 mediatypes we mix saturation
// and lightness to give the rare pairings (e.g. emerald-vs-teal, pink-vs-rose)
// enough perceptual distance that they can't be confused at a glance.

const MEDIA_TYPE_COLORS = {
	television: "#3B82F6",        // blue
	radio: "#F97316",             // orange
	online: "#10B981",            // emerald
	newspaper: "#DC2626",         // red
	magazine: "#9333EA",          // purple
	outdoor: "#06B6D4",           // cyan
	mobile: "#EC4899",            // pink
	"direct marketing": "#92400E",// brown
	directmarketing: "#92400E",   // alias (some callers strip spaces)
	podcast: "#475569",           // slate
	print: "#EAB308",             // yellow
	advertorial: "#0F766E",       // dark teal
	"social media": "#BE185D",    // wine
	socialmedia: "#BE185D",       // alias
};

const SENTIMENT_COLORS = {
	positive: "#16A34A",  // green
	negative: "#DC2626",  // red
	neutral: "#71717A",   // zinc
};

const SOURCE_COLORS = {
	twitter: "#1DA1F2",
	x: "#1DA1F2",
	youtube: "#FF0000",
	facebook: "#1877F2",
	instagram: "#E1306C",
	linkedin: "#0A66C2",
	tiktok: "#1F1F1F",
};

const FALLBACK = "#6B7280"; // neutral gray for unknown categories

const normalize = (s) => (s == null ? "" : String(s)).toLowerCase().trim();

const lookup = (table, name) => {
	const key = normalize(name);
	if (!key) return FALLBACK;
	if (table[key]) return table[key];
	const compact = key.replace(/\s+/g, "");
	if (table[compact]) return table[compact];
	return FALLBACK;
};

// Lighten a hex colour toward white by `amount` (0..1). Same hue, brighter.
const lighten = (hex, amount = 0.18) => {
	if (!hex || hex[0] !== "#" || hex.length !== 7) return hex;
	const num = parseInt(hex.slice(1), 16);
	const r = (num >> 16) & 0xff;
	const g = (num >> 8) & 0xff;
	const b = num & 0xff;
	const lr = Math.min(255, Math.round(r + (255 - r) * amount));
	const lg = Math.min(255, Math.round(g + (255 - g) * amount));
	const lb = Math.min(255, Math.round(b + (255 - b) * amount));
	return "#" + [lr, lg, lb].map((n) => n.toString(16).padStart(2, "0")).join("");
};

export const getMediaTypeColor = (mediatype) => lookup(MEDIA_TYPE_COLORS, mediatype);
export const getMediaTypeGradient = (mediatype) => {
	const base = getMediaTypeColor(mediatype);
	return [base, lighten(base)];
};

export const getSentimentColor = (sentiment) => lookup(SENTIMENT_COLORS, sentiment);
export const getSentimentGradient = (sentiment) => {
	const base = getSentimentColor(sentiment);
	return [base, lighten(base)];
};

export const getSourceColor = (source) => lookup(SOURCE_COLORS, source);
export const getSourceGradient = (source) => {
	const base = getSourceColor(source);
	return [base, lighten(base)];
};

export { MEDIA_TYPE_COLORS, SENTIMENT_COLORS, SOURCE_COLORS, FALLBACK };
